import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MessageCircle, Send, CheckCircle2, Loader2 } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const SUPPORT_EMAIL = 'support@tenses.ai';
const API_BASE = process.env.REACT_APP_API_URL;

const SUBJECTS = [
  'General enquiry',
  'Technical support',
  'Billing question',
  'Colleges & organisations',
  'Feedback'
];

/**
 * Contact.
 *
 * Deliberately just two ways through: the form, or email. No phone number and
 * no postal address.
 *
 * The form has no backend yet, so submitting opens the visitor's mail client
 * with everything pre-filled - the message genuinely reaches us instead of
 * vanishing into a handler that does not exist.
 */
const ContactPage = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: SUBJECTS[0],
    message: ''
  });

  const [error, setError] = useState('');

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  /**
   * Submit to the backend so the message lands in the admin dashboard's
   * Messages inbox. If the API cannot be reached we fall back to opening the
   * visitor's mail client, so an enquiry is never silently lost.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    try {
      const { data } = await axios.post(
        `${API_BASE}/contact-messages`,
        {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          subject: form.subject,
          message: form.message,
          source: 'website'
        },
        { timeout: 12000 }
      );

      if (data?.success === false) throw new Error(data?.error || 'submit failed');
      setSent(true);
    } catch (err) {
      console.error('Contact form submit failed:', err?.response?.data || err?.message);

      // Rate limited or rejected by validation: tell them what to fix.
      const status = err?.response?.status;
      if (status === 429) {
        setError('That is a lot of messages in a short time. Please wait a minute and try again.');
      } else if (status === 400) {
        setError(err?.response?.data?.error || 'Please check the form and try again.');
      } else {
        // Unreachable backend: hand off to email rather than lose the message.
        const name = `${form.firstName} ${form.lastName}`.trim();
        const body = [
          `Name: ${name || '(not given)'}`,
          `Email: ${form.email || '(not given)'}`,
          '',
          form.message
        ].join('\n');
        window.location.href =
          `mailto:${SUPPORT_EMAIL}` +
          `?subject=${encodeURIComponent(`[${form.subject}] ${name || 'Website enquiry'}`)}` +
          `&body=${encodeURIComponent(body)}`;
        setSent(true);
      }
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    border: '1px solid var(--border-medium)',
    borderRadius: '12px',
    background: '#fff'
  };

  return (
    <div className="min-h-screen">
      <Header />

      <main>
        <section className="section" style={{ paddingTop: 'clamp(7rem, 12vw, 9rem)' }}>
          <header className="section-header">
            <p className="eyebrow">Contact</p>
            <h1 className="heading-2 mt-3">Talk to us</h1>
            <p className="body-large">
              Questions about a plan, your score, or rolling Tenses out to a college or
              team? Send a message and we will get back to you.
            </p>
          </header>

          <div className="mx-auto grid max-w-[1000px] gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            {/* -------------------------- Email card -------------------------- */}
            <aside className="flex flex-col gap-4">
              <div className="card">
                <span
                  className="mb-4 grid h-11 w-11 place-items-center rounded-xl"
                  style={{ background: 'rgba(52,89,164,0.09)', color: 'var(--tenses-blue)' }}
                >
                  <Mail className="h-5 w-5" />
                </span>
                <h2 className="heading-3">Email us</h2>
                <p className="body-medium mt-2 text-[0.92rem]">
                  The fastest way to reach the team. We reply to every message.
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-4 inline-block text-[1.02rem] font-semibold"
                  style={{ color: 'var(--tenses-blue)' }}
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>

              <div
                className="rounded-[20px] p-6"
                style={{ background: 'var(--bg-section)', border: '1px solid var(--border-light)' }}
              >
                <span
                  className="mb-3 grid h-10 w-10 place-items-center rounded-xl"
                  style={{ background: '#fff', color: 'var(--tenses-blue)' }}
                >
                  <MessageCircle className="h-[18px] w-[18px]" />
                </span>
                <h3 className="text-[0.98rem] font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Response time
                </h3>
                <p className="mt-1.5 text-[0.88rem] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  We usually reply within one working day. Support requests about an active
                  plan are prioritised.
                </p>
              </div>
            </aside>

            {/* ----------------------------- Form ----------------------------- */}
            <div
              className="rounded-[22px] bg-white p-7 sm:p-8"
              style={{ border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-md)' }}
            >
              {sent ? (
                <div className="flex flex-col items-center py-12 text-center">
                  <span
                    className="mb-5 grid h-14 w-14 place-items-center rounded-2xl"
                    style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
                  >
                    <CheckCircle2 className="h-7 w-7" />
                  </span>
                  <h2 className="heading-3">Message sent</h2>
                  <p className="body-medium mt-2 max-w-[360px] text-[0.92rem]">
                    Thanks for getting in touch. We will reply to your email address. If you need us sooner,
                    write to us directly at{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--tenses-blue)', fontWeight: 600 }}>
                      {SUPPORT_EMAIL}
                    </a>
                    .
                  </p>
                  <button type="button" onClick={() => setSent(false)} className="btn-secondary mt-7">
                    Write another message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="heading-3">Send a message</h2>

                  {error && (
                    <div
                      role="alert"
                      className="mt-4 rounded-xl px-4 py-3 text-[0.88rem]"
                      style={{
                        background: 'rgba(220,38,38,0.06)',
                        border: '1px solid rgba(220,38,38,0.22)',
                        color: '#b91c1c'
                      }}
                    >
                      {error}
                    </div>
                  )}
                  <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-2 block text-[0.85rem] font-medium" style={{ color: 'var(--text-primary)' }}>
                          First name
                        </span>
                        <input
                          type="text"
                          required
                          value={form.firstName}
                          onChange={update('firstName')}
                          className="w-full px-4 py-3 outline-none transition-shadow focus:ring-2"
                          style={inputStyle}
                          placeholder="Your first name"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-2 block text-[0.85rem] font-medium" style={{ color: 'var(--text-primary)' }}>
                          Last name
                        </span>
                        <input
                          type="text"
                          value={form.lastName}
                          onChange={update('lastName')}
                          className="w-full px-4 py-3 outline-none transition-shadow focus:ring-2"
                          style={inputStyle}
                          placeholder="Your last name"
                        />
                      </label>
                    </div>

                    <label className="block">
                      <span className="mb-2 block text-[0.85rem] font-medium" style={{ color: 'var(--text-primary)' }}>
                        Email address
                      </span>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update('email')}
                        className="w-full px-4 py-3 outline-none transition-shadow focus:ring-2"
                        style={inputStyle}
                        placeholder="you@example.com"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-[0.85rem] font-medium" style={{ color: 'var(--text-primary)' }}>
                        Subject
                      </span>
                      <select
                        value={form.subject}
                        onChange={update('subject')}
                        className="w-full px-4 py-3 outline-none transition-shadow focus:ring-2"
                        style={inputStyle}
                      >
                        {SUBJECTS.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-[0.85rem] font-medium" style={{ color: 'var(--text-primary)' }}>
                        Message
                      </span>
                      <textarea
                        rows="5"
                        required
                        value={form.message}
                        onChange={update('message')}
                        className="w-full resize-none px-4 py-3 outline-none transition-shadow focus:ring-2"
                        style={inputStyle}
                        placeholder="How can we help?"
                      />
                    </label>

                    <button type="submit" className="btn-primary w-full" disabled={sending}>
                      {sending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Send message
                          <Send className="h-4 w-4" />
                        </>
                      )}
                    </button>

                    <p className="text-center text-[0.8rem]" style={{ color: 'var(--text-muted)' }}>
                      Prefer email? Write to{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--tenses-blue)', fontWeight: 600 }}>
                        {SUPPORT_EMAIL}
                      </a>
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
